import { convertFromRaw, RawDraftContentState } from "draft-js";

export const EMPTY_RAW_DRAFT_CONTENT_STATE: RawDraftContentState = {
  blocks: [],
  entityMap: {}
};

const plainTextToRawDraft = (text: string): RawDraftContentState => ({
  blocks: [
    {
      key: "plain",
      text,
      type: "unstyled",
      depth: 0,
      inlineStyleRanges: [],
      entityRanges: [],
      data: {}
    }
  ],
  entityMap: {}
});

const tryParseWriteupJson = (value: string): unknown => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  let parsed: unknown = JSON.parse(trimmed.replace(/'/g, '"'));

  if (typeof parsed === "string") {
    const inner = parsed.trim();
    if (!inner) {
      return null;
    }

    try {
      parsed = JSON.parse(inner);
    } catch {
      return plainTextToRawDraft(String(parsed));
    }
  }

  return parsed;
};

export const isValidRawDraftContentState = (
  value: unknown
): value is RawDraftContentState => {
  if (!value || typeof value !== "object") {
    return false;
  }

  try {
    convertFromRaw(value as RawDraftContentState);
    return true;
  } catch {
    return false;
  }
};

export const parseRawDraftContentState = (
  value?: string | null
): RawDraftContentState => {
  if (!value || value === "null") {
    return EMPTY_RAW_DRAFT_CONTENT_STATE;
  }

  try {
    const parsed = tryParseWriteupJson(value);

    if (isValidRawDraftContentState(parsed)) {
      return parsed;
    }

    if (typeof parsed === "string" && parsed.trim()) {
      return plainTextToRawDraft(parsed);
    }
  } catch {
    const trimmed = value.trim();
    if (trimmed) {
      return plainTextToRawDraft(trimmed);
    }
  }

  return EMPTY_RAW_DRAFT_CONTENT_STATE;
};

export const getSafeRawContentState = (
  initial?: RawDraftContentState | string | null
): RawDraftContentState | null => {
  if (initial === null || initial === undefined || initial === "") {
    return null;
  }

  const raw =
    typeof initial === "string"
      ? parseRawDraftContentState(initial)
      : isValidRawDraftContentState(initial)
      ? initial
      : null;

  if (!raw) {
    return null;
  }

  return raw.blocks.length > 0 || Object.keys(raw.entityMap).length > 0
    ? raw
    : null;
};
